import React, { useState, useEffect } from 'react';
import { FaTimes, FaEye, FaReceipt, FaMoneyBill } from 'react-icons/fa';
import './DetalleCompraModal.css';
import comprasService from '../../services/ComprasService';

const DetalleCompraModal = ({ isOpen, onClose, compra }) => {
  const [detalleIngredientes, setDetalleIngredientes] = useState([]);
  const [abonos, setAbonos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabActiva, setTabActiva] = useState('ingredientes'); // 'ingredientes' o 'abonos'

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (isOpen && compra) {
      cargarDetalleCompleto();
    }
  }, [isOpen, compra]);

  const cargarDetalleCompleto = async () => {
    if (!compra?.id) return;
    
    setLoading(true);
    setError('');

    try {
      // Cargar ingredientes y abonos en paralelo
      const [detalleData, abonosData] = await Promise.all([
        comprasService.getCompraDetalle(compra.id),
        comprasService.getAbonosCompra(compra.id)
      ]);

      setDetalleIngredientes(detalleData);
      setAbonos(abonosData);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      setError('Error al cargar el detalle de la compra: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Cerrar modal y limpiar datos
  const cerrarModal = () => {
    setDetalleIngredientes([]);
    setAbonos([]);
    setError('');
    setTabActiva('ingredientes');
    onClose();
  };

  if (!isOpen || !compra) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container detalle-compra-modal">
        <div className="modal-header">
          <h2>
            <FaEye /> Detalle de Compra {compra.compra}
          </h2>
          <button className="btn-close" onClick={cerrarModal}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-content">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Información general de la compra */}
          <div className="compra-resumen">
            <div className="resumen-grid">
              <div className="resumen-item">
                <label>Compra:</label>
                <span className="valor-destacado">{compra.compra}</span>
              </div>
              <div className="resumen-item">
                <label>Proveedor:</label>
                <span>{compra.proveedor}</span>
              </div>
              <div className="resumen-item">
                <label>Referencia:</label>
                <span>{compra.referencia}</span>
              </div>
              <div className="resumen-item">
                <label>Fecha:</label>
                <span>{compra.fecha}</span>
              </div>
              <div className="resumen-item">
                <label>Monto Total:</label>
                <span className="monto-total">${compra.monto?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="resumen-item">
                <label>Abonado:</label>
                <span className="monto-abonado">${compra.abonado?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="resumen-item">
                <label>Saldo:</label>
                <span className="saldo-pendiente">${compra.saldo?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="resumen-item">
                <label>Estatus:</label>
                <span className={`estatus ${compra.estatus?.toLowerCase()}`}>
                  {compra.estatus}
                </span>
              </div>
            </div>
          </div>

          {/* Pestañas de navegación */}
          <div className="tabs-container">
            <div className="tabs-header">
              <button 
                className={`tab-button ${tabActiva === 'ingredientes' ? 'active' : ''}`}
                onClick={() => setTabActiva('ingredientes')}
              >
                <FaReceipt /> Ingredientes ({detalleIngredientes.length})
              </button>
              <button 
                className={`tab-button ${tabActiva === 'abonos' ? 'active' : ''}`}
                onClick={() => setTabActiva('abonos')}
              >
                <FaMoneyBill /> Abonos ({abonos.length})
              </button>
            </div>

            <div className="tabs-content">
              {loading ? (
                <div className="loading-content">
                  <div className="spinner"></div>
                  <p>Cargando información...</p>
                </div>
              ) : (
                <>
                  {/* Pestaña de Ingredientes */}
                  {tabActiva === 'ingredientes' && (
                    <div className="tab-panel ingredientes-panel">
                      {detalleIngredientes.length === 0 ? (
                        <div className="sin-datos">
                          <FaReceipt />
                          <p>No hay ingredientes registrados para esta compra</p>
                        </div>
                      ) : (
                        <div className="ingredientes-tabla">
                          <div className="tabla-header">
                            <div>Ingrediente</div>
                            <div>Cantidad</div>
                            <div>Costo Unit.</div>
                            <div>Importe</div>
                          </div>
                          
                          {detalleIngredientes.map((item, index) => (
                            <div key={item.idCompraDetalle || index} className="tabla-row">
                              <div className="campo-ingrediente">
                                <span className="nombre-ingrediente">{item.nombre}</span>
                              </div>
                              <div className="campo-cantidad">
                                {parseFloat(item.cantidad).toLocaleString()}
                              </div>
                              <div className="campo-costo">
                                ${parseFloat(item.costo).toFixed(2)}
                              </div>
                              <div className="campo-importe">
                                ${(parseFloat(item.cantidad) * parseFloat(item.costo)).toFixed(2)}
                              </div>
                            </div>
                          ))}
                          
                          {/* Total de ingredientes */}
                          <div className="tabla-footer">
                            <div className="total-ingredientes">
                              <strong>
                                Total: ${detalleIngredientes
                                  .reduce((total, item) => total + (parseFloat(item.cantidad) * parseFloat(item.costo)), 0)
                                  .toFixed(2)
                                }
                              </strong>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pestaña de Abonos */}
                  {tabActiva === 'abonos' && (
                    <div className="tab-panel abonos-panel">
                      {abonos.length === 0 ? (
                        <div className="sin-datos">
                          <FaMoneyBill />
                          <p>No hay abonos registrados para esta compra</p>
                        </div>
                      ) : (
                        <div className="abonos-tabla">
                          <div className="tabla-header">
                            <div>Fecha</div>
                            <div>Monto</div>
                            <div>Referencia</div>
                          </div>
                          
                          {abonos.map((abono, index) => (
                            <div key={abono.idAbono || index} className="tabla-row">
                              <div className="campo-fecha">
                                {abono.fecha}
                              </div>
                              <div className="campo-monto">
                                <span className="monto-abono">
                                  ${parseFloat(abono.monto).toFixed(2)}
                                </span>
                              </div>
                              <div className="campo-referencia">
                                {abono.referencia}
                              </div>
                            </div>
                          ))}
                          
                          {/* Total de abonos */}
                          <div className="tabla-footer">
                            <div className="total-abonos">
                              <strong>
                                Total Abonado: ${abonos
                                  .reduce((total, abono) => total + parseFloat(abono.monto), 0)
                                  .toFixed(2)
                                }
                              </strong>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            type="button" 
            className="btn-cerrar" 
            onClick={cerrarModal}
          >
            <FaTimes /> Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetalleCompraModal;